import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, consulta } from '../api/cliente';
import type { Pagina } from '../api/cliente';
import type { CitaResumen } from '../api/tipos';
import { useSesion } from '../auth/sesion';
import {
  Aviso,
  Campo,
  Cargando,
  Encabezado,
  Etiqueta,
  Paginador,
  Seleccion,
  Tarjeta,
  Vacio,
  fechaHora,
} from '../ui/piezas';

const ESTADOS = ['PENDIENTE', 'CONFIRMADA', 'REPROGRAMADA', 'CANCELADA', 'COMPLETADA', 'NO_ASISTIO'];

/** Hoy, para que la agenda abra en lo que viene y no en el historial. */
function hoy(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function Agenda() {
  const cache = useQueryClient();
  const { puede } = useSesion();
  const [desde, setDesde] = useState(hoy());
  const [hasta, setHasta] = useState('');
  const [estado, setEstado] = useState('');
  const [pagina, setPagina] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const [exito, setExito] = useState<string | null>(null);

  const listado = useQuery({
    queryKey: ['citas', desde, hasta, estado, pagina],
    queryFn: () =>
      api<Pagina<CitaResumen>>(
        `/admin/agenda/citas${consulta({ desde, hasta, estado, page: pagina, size: 50 })}`,
      ),
  });

  const cerrar = useMutation({
    mutationFn: ({ id, destino }: { id: string; destino: string }) =>
      api<unknown>(`/admin/agenda/citas/${id}/estado/${destino}`, { metodo: 'POST' }),
    onSuccess: () => {
      setError(null);
      setExito('Visita cerrada.');
      void cache.invalidateQueries({ queryKey: ['citas'] });
    },
    onError: (e) => {
      setExito(null);
      setError(e);
    },
  });

  const filas = listado.data?.content ?? [];

  return (
    <>
      <Encabezado
        titulo="Agenda"
        nota="Las visitas reservadas desde el portal y las que agenda la oficina."
      />

      <Aviso error={error ?? listado.error} exito={exito} />

      <Tarjeta>
        <div className="rejilla" style={{ marginBottom: 16 }}>
          <Campo etiqueta="Desde" tipo="date" valor={desde} alCambiar={setDesde} />
          <Campo etiqueta="Hasta" tipo="date" valor={hasta} alCambiar={setHasta} />
          <Seleccion
            etiqueta="Estado"
            valor={estado}
            alCambiar={(v) => {
              setEstado(v);
              setPagina(0);
            }}
            vacio="Todos"
            opciones={ESTADOS.map((e) => ({
              valor: e,
              texto: e.replaceAll('_', ' ').toLowerCase(),
            }))}
          />
        </div>

        {listado.isPending ? (
          <Cargando />
        ) : filas.length === 0 ? (
          <Vacio>No hay visitas en ese rango.</Vacio>
        ) : (
          <div className="tabla-marco">
            <table>
              <thead>
                <tr>
                  <th>Cuándo</th>
                  <th>Inmueble</th>
                  <th>Visitante</th>
                  <th>Dónde</th>
                  <th>Asesor</th>
                  <th>Estado</th>
                  {puede('cita:gestionar') && <th />}
                </tr>
              </thead>
              <tbody>
                {filas.map((c) => (
                  <tr key={c.id}>
                    <td>{fechaHora(c.inicio)}</td>
                    <td>{c.referencia}</td>
                    <td>
                      <div>{c.visitante}</div>
                      <small style={{ opacity: 0.7 }}>{c.contacto ?? '—'}</small>
                    </td>
                    <td>{c.lugar}</td>
                    <td>{c.agente}</td>
                    <td>
                      <Etiqueta>{c.estado}</Etiqueta>
                    </td>
                    {puede('cita:gestionar') && (
                      <td>
                        {['PENDIENTE', 'CONFIRMADA', 'REPROGRAMADA'].includes(c.estado) && (
                          <>
                            <button
                              className="plano"
                              onClick={() => cerrar.mutate({ id: c.id, destino: 'COMPLETADA' })}
                            >
                              Se hizo
                            </button>
                            <button
                              className="plano"
                              onClick={() => cerrar.mutate({ id: c.id, destino: 'NO_ASISTIO' })}
                            >
                              No llegó
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Paginador
          pagina={listado.data?.number ?? 0}
          total={listado.data?.totalElements ?? 0}
          paginas={listado.data?.totalPages ?? 0}
          alCambiar={setPagina}
        />
        <p className="campo__ayuda" style={{ marginTop: 10 }}>
          «No llegó» y «cancelada» son cosas distintas a propósito: cancelar con tiempo libera la
          hora, y no presentarse costó un desplazamiento y una mañana. Cancelar exige motivo y
          todavía se hace por la API.
        </p>
      </Tarjeta>
    </>
  );
}
